"use client"

import React, {useEffect, useRef, useState} from 'react';
import cx from "clsx";
import {BackgroundImage, Button, Modal, UnstyledButton} from "@mantine/core";
import {Crete_Round, Fugaz_One, Golos_Text, Noto_Sans_TC, Poppins, Rowdies} from 'next/font/google';
import useGlobalStateStore from "@/app/(index)/use-global-state-store";
import {loadIndexArticleList, loadIndexCarouselList} from "@/service";
import {getPublicAlertSetting} from "@/service/alert";
import {IArticleListForm} from "@/form/article";
import "./CustomDots.css";
import {useDisclosure, useMediaQuery} from "@mantine/hooks";
import Link from "next/link";
import Footer from "@/app/(index)/footer";
import Header2 from "@/app/(index)/header2";
import {IconCircleArrowRightFilled, IconPlus} from '@tabler/icons-react';
import HomeNewsCard from "@/app/(index)/home-news";
import '@mantine/carousel/styles.css';
import {Carousel} from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import HomeCarouselLg from "@/app/(index)/home-carousel-lg";
import HomeCarouselSm from "@/app/(index)/home-carousel-sm";

const poppins = Poppins({
    weight: ['400', '500', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

const notoSansTC = Noto_Sans_TC({
    weight: ['400', '500', '700', '800'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});


const fugazOne = Fugaz_One({
    weight: ['400'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

const creteRound = Crete_Round({
    weight: ['400'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

const rowdies = Rowdies({
    weight: ['300', '400', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});


const golosText = Golos_Text({
    weight: ['600', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});


export default function HomePage() {
    const { fontSize, lang } = useGlobalStateStore();
    const [homeArticles, setHomeArticles] = useState<{[key: string]: IArticleListForm[]}>({});
    const [carouselList, setCarouselList] = useState<IArticleListForm[]>([]);
    const mediaQueryLessSM = useMediaQuery('(min-width: 480px)');

    const [opened, {open, close}] = useDisclosure(false);
    const [opened2, {open: open2, close: close2}] = useDisclosure(false);
    const alertShownRef = useRef(false);

    const loadCurrentFontSize = (s: number, m: number, l: number) => {
        if(fontSize == 0) return `text-[${s}px]`;
        else if(fontSize == 1) return `text-[${m}px]`;
        else if(fontSize == 2) return `text-[${l}px]`;
    }

    useEffect(() => {
        if(lang == "en"){
            open();
        }
        loadIndexCarouselList(lang).then(data => {
            setCarouselList(data);
        })
    }, [lang]);

    useEffect(() => {
        loadIndexArticleList().then(data => {
            setHomeArticles(data);
        });

        // 檢查警報設定
        if (!alertShownRef.current) {
            getPublicAlertSetting().then(data => {
              console.log(data);
                if (data.enabled && data.startTime && data.endTime && !alertShownRef.current) {
                    const now = new Date();
                    const start = new Date(data.startTime);
                    const end = new Date(data.endTime);
                    if (now >= start && now <= end && data.content) {
                        alertShownRef.current = true;
                        alert(data.content);
                    }
                }
            });
        }
    }, []);

    const autoplay = useRef(Autoplay({ delay: 5000 }));
    return (
        <div className={cx("min-h-screen flex flex-col justify-between", notoSansTC.className)}>

            <Modal opened={opened}
                   onClose={close}
                   closeOnClickOutside={false}
                   closeOnEscape={false}
                   withCloseButton={false}
                   zIndex={9999}
            >
                <div className="flex flex-col gap-3">
                    <div>
                        This website has a privacy policy to protect your personal information.
                        Please click "I agree" to indicate your consent to this website's privacy policy. For more information,
                        please click <Link href={"/privacy-policy"} target="_blank" className="text-blue-600 underline">here</Link>.
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={close} color="indigo">I Agree</Button>
                    </div>
                </div>
            </Modal>
            <Modal opened={opened2}
                   onClose={close2}
                   closeOnClickOutside={false}
                   closeOnEscape={false}
                   withCloseButton={false}
                   zIndex={9999}
            >
                <div className="flex flex-col gap-3">
                    <div>
                        因應國研院網域變更，本中心官網網域已由 「www.stpi.narl.org.tw」 正式變更為 「www.stpi.niar.org.tw」
                        敬請各位使用者更新瀏覽器書籤（Bookmark）設定，以確保正常存取本中心網站與相關服務。
                    </div>
                    <div className="flex justify-center">
                        <Button onClick={close2} color="indigo">關閉</Button>
                    </div>
                </div>
            </Modal>
            <Header2/>
            <div className={cx("relative", mediaQueryLessSM ? "mt-[100px] h-[80vh]" : "mt-[80px] h-[90vh]")}>
                {
                    mediaQueryLessSM ? (
                        <>
                            <HomeCarouselLg carouselList={carouselList}/>
                            <div className="absolute bottom-0 left-0 w-full bg-black bg-opacity-70">
                                <HomeNewsCard homeArticles={homeArticles} />
                            </div>
                        </>
                    ) : (
                        <HomeCarouselSm carouselList={carouselList}/>
                    )
                }
            </div>
            {
                !mediaQueryLessSM && (
                    <div className="w-full bg-black bg-opacity-90">
                        <HomeNewsCard homeArticles={homeArticles} />
                    </div>
                )
            }

            <div className="bg-[#f1f7f5] w-full relative">
                <div className="max-w-[1250px] mx-auto py-[30px]">
                    <div className={cx("w-[94%]", mediaQueryLessSM ? "" : "mx-auto")}>
                        <Carousel loop
                                  plugins={[autoplay.current]}
                                  onMouseEnter={autoplay.current.stop}
                                  onMouseLeave={autoplay.current.reset}
                                  withControls={false}
                        >
                            {
                                lang == "tw" ? (
                                    homeArticles["listC_EVENT"]?.map((item, index) => (
                                        <Carousel.Slide>
                                            <UnstyledButton component={"a"} key={`event-c-${index}`} href={item.category == "LINK" ?  item.linkUrl!! : `/news/detail/${item.artId}`}>
                                                <div className={"flex"}>
                                                    <div className={cx(loadCurrentFontSize(15, 20, 25), "font-bold text-[#adadad] pr-[20px]")}>{item.publishDate}</div>
                                                    <div className={cx(loadCurrentFontSize(15, 20, 25), "border-l border-[#d4e7eb] text-[#158a83] hover:text-black transition pl-[20px] font-bold")}>{item.titleC}</div>
                                                </div>
                                            </UnstyledButton>
                                        </Carousel.Slide>
                                    ))
                                ) : (
                                    homeArticles["listE_EVENT"]?.map((item, index) => (
                                        <Carousel.Slide>
                                            <UnstyledButton component={"a"} key={`event-e-${index}`} href={item.category == "LINK" ?  item.linkUrl!! : `/news/detail/${item.artId}`}>
                                                <div className={"flex"}>
                                                    <div className={cx(loadCurrentFontSize(15, 20, 25), "font-bold text-[#adadad] pr-[20px]")}>{item.publishDate}</div>
                                                    <div className={cx(loadCurrentFontSize(15, 20, 25), "border-l border-[#d4e7eb] text-[#158a83] hover:text-black transition pl-[20px] font-bold")}>{item.titleE}</div>
                                                </div>
                                            </UnstyledButton>
                                        </Carousel.Slide>
                                    ))
                                )
                            }
                        </Carousel>
                    </div>
                </div>
                <UnstyledButton className="absolute right-[3%] bottom-[10px]" component="a" href={"news/list/B"}>
                    <div className="flex gap-1 items-center">
                        <div className="font-bold text-[#818281] hover:text-[#158a83] text-[14px]">{lang == "tw" ? "更多活動" : "more events"}</div>
                        <IconPlus color={"#1f8a83"} stroke={3} size={16}/>
                    </div>
                </UnstyledButton>
            </div>
            <Footer/>
        </div>
    );
}